import React, { useState } from 'react';
import './App.css';
import AppHeader, { NavPage } from './components/layout/AppHeader';
import LoginScreen from './components/auth/LoginScreen';
import Dashboard from './components/dashboard/Dashboard';
import PerformanceTrackerPage from './components/performance/PerformanceTrackerPage';
import TimeTrackerPage from './components/time-tracker/TimeTrackerPage';
import TeamDashboard from './components/team-dashboard/TeamDashboard';
import EstimateReviewPage from './components/estimate-review/EstimateReviewPage';
import JiraPage from './components/jira/JiraPage';
import ReportsPage from './components/reports/ReportsPage';
import ProjectReportsPage from './components/reports/ProjectReportsPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { OrgThemeProvider, useOrgTheme } from './context/OrgThemeContext';
import { NotificationProvider, ToastContainer } from './components/notifications';

// Main App with navigation
const MainApp: React.FC = () => {
  const { loading: orgLoading } = useOrgTheme();
  const [currentPage, setCurrentPage] = useState<NavPage>('dashboard');

  if (orgLoading) {
    return (
      <div className="app-container">
        <div className="loading">
          <div className="spinner"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard />;
      case 'performance':
        return <PerformanceTrackerPage />;
      case 'time-tracker':
        return <TimeTrackerPage />;
      case 'team-dashboard':
        return <TeamDashboard />;
      case 'estimate-review':
        return <EstimateReviewPage />;
      case 'jira':
        return <JiraPage />;
      case 'reports':
        return <ReportsPage />;
      case 'project-reports':
        return <ProjectReportsPage />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="app-container">
      <AppHeader currentPage={currentPage} onNavigate={setCurrentPage} />
      <main className="main-content">
        {renderPage()}
      </main>
      <ToastContainer />
    </div>
  );
};

// Wrapper component that handles auth state
const AppContent: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="app-container">
        <div className="loading">
          <div className="spinner"></div>
          <span>Checking authentication...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  return <MainApp />;
};

// Main App with AuthProvider and NotificationProvider
const App: React.FC = () => {
  return (
    <AuthProvider>
      <OrgThemeProvider>
        <NotificationProvider>
          <AppContent />
        </NotificationProvider>
      </OrgThemeProvider>
    </AuthProvider>
  );
};

export default App;
